import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/layout/Layout';
import {
  Shield,
  Calendar,
  CheckCircle,
  AlertTriangle,
  Car,
  User,
  Download,
  Clock
} from 'lucide-react';
import { formatDate } from '../utils/helpers';
import { mockApi } from '../services/mockApi';

const ClienteGarantiaPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [garantia, setGarantia] = useState(null);

  const cargarGarantia = useCallback(async () => {
    try {
      setLoading(true);
      const clienteRes = await mockApi.getClienteByEmail(user.email);
      const data = await mockApi.getGarantiaCliente(clienteRes.data.id);
      setGarantia(data);
    } catch (error) {
      console.error('Error al cargar garantía:', error);
    } finally {
      setLoading(false);
    }
  }, [user.email]);

  useEffect(() => {
    if (user) {
      cargarGarantia();
    }
  }, [user, cargarGarantia]);

  const descargarCertificado = async () => {
    try {
      const clienteRes = await mockApi.getClienteByEmail(user.email);
      await mockApi.descargarCertificadoPDF(clienteRes.data.id, garantia.garantia.id);
      alert('Certificado descargado correctamente');
    } catch (error) {
      console.error('Error al descargar certificado:', error);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Cargando información de garantía...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!garantia) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <p className="text-gray-600">No se encontró información de garantía</p>
          </div>
        </div>
      </Layout>
    );
  }

  const { garantia: garantiaData, vehiculo, cobertura } = garantia.data;
  const diasRestantes = garantiaData.diasRestantes || 0;
  const estaProximoVencer = garantiaData.proximaVencer || false;

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4 md:p-8">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Mi Garantía
              </h1>
              <p className="text-gray-600 mt-1">Información detallada de tu garantía extendida</p>
            </div>
          </div>

          {estaProximoVencer && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
              <div className="flex items-center">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mr-3" />
                <div>
                  <p className="text-yellow-800 font-medium">Tu garantía está próxima a vencer</p>
                  <p className="text-yellow-700 text-sm">Quedan {diasRestantes} días. Contacta con nosotros para renovarla.</p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/20">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl">
                      <Shield className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-800">Información de Garantía</h2>
                      <p className="text-sm text-gray-500">Nº {garantiaData.numeroGarantia}</p>
                    </div>
                  </div>
                  <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                    garantiaData.estado === 'activa' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {garantiaData.estado === 'activa' ? '✓ Activa' : garantiaData.estado}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start space-x-3 p-4 bg-blue-50 rounded-xl">
                    <Calendar className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-600">Fecha de inicio</p>
                      <p className="font-semibold text-gray-800">{formatDate(garantiaData.fechaAlta)}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-4 bg-purple-50 rounded-xl">
                    <Clock className="h-5 w-5 text-purple-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-600">Fecha de fin</p>
                      <p className="font-semibold text-gray-800">{formatDate(garantiaData.fechaVencimiento)}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Días restantes</p>
                      <p className="text-2xl font-bold text-gray-800">{diasRestantes > 0 ? diasRestantes : 0}</p>
                    </div>
                    <button
                      onClick={descargarCertificado}
                      className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all"
                    >
                      <Download className="h-4 w-4" />
                      <span>Descargar Certificado</span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/20">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="p-3 bg-gradient-to-br from-green-500 to-teal-600 rounded-xl">
                    <Car className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">Vehículo Asegurado</h2>
                    <p className="text-sm text-gray-500">Información del vehículo cubierto</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <p className="text-sm text-gray-600">Marca y Modelo</p>
                    <p className="font-semibold text-gray-800">{vehiculo.marca} {vehiculo.modelo}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <p className="text-sm text-gray-600">Matrícula</p>
                    <p className="font-semibold text-gray-800">{vehiculo.matricula}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <p className="text-sm text-gray-600">Año</p>
                    <p className="font-semibold text-gray-800">{vehiculo.year}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <p className="text-sm text-gray-600">Kilometraje</p>
                    <p className="font-semibold text-gray-800">{vehiculo.kilometros?.toLocaleString() || 0} km</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/20">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl">
                    <CheckCircle className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">Cobertura</h2>
                    <p className="text-sm text-gray-500">Componentes y servicios incluidos</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-3">Componentes Cubiertos</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {cobertura.componentesCubiertos.map((componente, index) => (
                        <div key={index} className="flex items-center space-x-2 p-3 bg-green-50 rounded-lg">
                          <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{componente}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-800 mb-3">Servicios Incluidos</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {cobertura.serviciosIncluidos.map((servicio, index) => (
                        <div key={index} className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg">
                          <CheckCircle className="h-4 w-4 text-blue-600 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{servicio}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/20">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="p-3 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl">
                    <User className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">Titular</h2>
                    <p className="text-sm text-gray-500">Datos del propietario</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600">Nombre completo</p>
                    <p className="font-semibold text-gray-800">{user.nombre}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-semibold text-gray-800">{user.email}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl shadow-xl p-6 text-white">
                <h3 className="font-bold text-lg mb-4">¿Necesitas ayuda?</h3>
                <p className="text-sm text-blue-100 mb-4">
                  Nuestro equipo está disponible para resolver cualquier duda sobre tu garantía.
                </p>
                <div className="space-y-2 text-sm">
                  <p>📞 900 123 456</p>
                  <p>📧 soporte@rdgarantia.com</p>
                  <p>🕐 Lun-Vie: 9:00 - 18:00</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ClienteGarantiaPage;
